import { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Home,
  Plus,
  Trash2,
  Pencil,
  MapPin,
  Phone,
  Mail,
  Wrench,
} from "lucide-react";
import {
  formatMoney,
  ORDER_STATUS_LABELS,
  PROPERTY_SYSTEM_TYPE_LABELS,
  SALUTATIONS,
  SALUTATION_LABELS,
} from "@mavatech/shared";
import { useUnsavedGuard } from "@/lib/useUnsavedGuard";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { AddressDialog } from "./AddressDialog";
import { ContactDialog } from "./ContactDialog";
import { JournalSection } from "./JournalSection";
import { PropertyDialog } from "@/pages/Contracts/PropertyDialog";
import { OrderDialog } from "@/pages/Orders/OrderDialog";

const ORDER_STATUS_TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  quoted: "neutral",
  ordered: "accent",
  done: "success",
  cancelled: "danger",
};

const QUOTE_STATUS_LABEL: Record<string, string> = {
  draft: "Entwurf",
  sent: "gesendet",
  accepted: "angenommen",
  rejected: "abgelehnt",
  expired: "abgelaufen",
};
const INVOICE_STATUS_LABEL: Record<string, string> = {
  draft: "Entwurf",
  sent: "gesendet",
  partially_paid: "teilbezahlt",
  paid: "bezahlt",
  overdue: "überfällig",
  cancelled: "storniert",
};

const ADDR_KIND_LABEL: Record<string, string> = {
  billing: "Rechnungsadresse",
  site: "Objekt / Baustelle",
  postal: "Postanschrift",
};

export function CustomerDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const q = trpc.customer.get.useQuery({ id }, { retry: false });
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [addrDialog, setAddrDialog] = useState<{ open: boolean; edit?: any }>({
    open: false,
  });
  const [contactDialog, setContactDialog] = useState<{
    open: boolean;
    edit?: any;
  }>({ open: false });
  const [propDialog, setPropDialog] = useState<{
    open: boolean;
    propertyId?: string | null;
  }>({ open: false });
  // Geöffneter Auftrag steht in der URL (?order=<id>) — "Zurück" aus einem Dokument, das über das
  // Auftragsjournal geöffnet wurde, führt so wieder in denselben Auftrag.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlOrderId = searchParams.get("order");
  const [orderDialogLocal, setOrderDialog] = useState<{
    open: boolean;
    orderId?: string | null;
  }>({ open: false });
  const orderDialog = urlOrderId
    ? { open: true, orderId: urlOrderId }
    : orderDialogLocal;
  const openOrder = (orderId: string) =>
    setSearchParams({ order: orderId }, { replace: true });
  const closeOrderDialog = () => {
    setOrderDialog({ open: false });
    if (urlOrderId) setSearchParams({}, { replace: true });
  };
  const properties = trpc.property.list.useQuery(
    { customerId: id },
    { enabled: !!id },
  );
  const removeProperty = trpc.property.remove.useMutation({
    onSuccess: () => utils.property.list.invalidate({ customerId: id }),
  });
  const quotes = trpc.quote.list.useQuery(
    { customerId: id },
    { enabled: !!id },
  );
  const invoices = trpc.invoice.list.useQuery(
    { customerId: id },
    { enabled: !!id },
  );
  const orders = trpc.order.list.useQuery(
    { customerId: id },
    { enabled: !!id },
  );

  const [form, setForm] = useState<Record<string, any>>({});
  // Formular nur beim ERSTEN Laden dieses Kunden aus den Serverdaten füllen. Jedes spätere Nachladen
  // (z. B. nach dem Hinzufügen einer Adresse/eines Ansprechpartners/Objekts) würde sonst die noch nicht
  // gespeicherten Eingaben (Name, E-Mail, …) mit den alten Serverwerten überschreiben — der Kunde blieb
  // dann "Neuer Kunde" ohne Daten.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (q.data && loadedFor.current !== q.data.id) {
      loadedFor.current = q.data.id;
      const d = q.data;
      setForm({
        kind: d.kind,
        salutation: d.salutation ?? "",
        name: d.name,
        firstName: d.firstName ?? "",
        lastName: d.lastName ?? "",
        companyName: d.companyName ?? "",
        customerNumber: d.customerNumber ?? "",
        vatId: d.vatId ?? "",
        isBaubetrieb: d.isBaubetrieb,
        email: d.email ?? "",
        phone: d.phone ?? "",
        website: d.website ?? "",
        paymentTermsDays: d.paymentTermsDays ?? "",
        isActive: d.isActive,
        notes: d.notes ?? "",
      });
    }
  }, [q.data]);

  // Warnung bei ungespeicherten Eingaben (Zurück, Menü, Seite schließen …), siehe lib/useUnsavedGuard.tsx
  const guard = useUnsavedGuard({
    ready: !!q.data && Object.keys(form).length > 0,
    getSnapshot: () => JSON.stringify(form),
  });
  const leave = (to: string) => {
    guard.allowLeave();
    navigate(to);
  };

  const save = trpc.customer.update.useMutation({
    onSuccess: () => {
      guard.markSaved();
      utils.customer.get.invalidate({ id });
      utils.customer.list.invalidate();
      addToast({ type: "success", title: "Gespeichert" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const archive = trpc.customer.archive.useMutation({
    onSuccess: () => {
      utils.customer.list.invalidate();
      addToast({ type: "success", title: "Kunde archiviert" });
      leave("/kunden");
    },
  });
  const removeAddress = trpc.customer.removeAddress.useMutation({
    onSuccess: () => utils.customer.get.invalidate({ id }),
  });
  const removeContact = trpc.customer.removeContact.useMutation({
    onSuccess: () => utils.customer.get.invalidate({ id }),
  });
  const createProperty = trpc.property.create.useMutation({
    onSuccess: () => {
      utils.property.list.invalidate({ customerId: id });
      addToast({ type: "success", title: "Objekt aus Adresse angelegt" });
    },
  });

  if (q.isLoading)
    return <p className="p-6 text-sm text-text-tertiary">Lädt …</p>;
  if (!q.data)
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate("/kunden")}>
          <ArrowLeft className="w-4 h-4" /> Zurück
        </Button>
        <p className="mt-4 text-sm text-text-tertiary">Kunde nicht gefunden.</p>
      </div>
    );

  const c = q.data;
  const set = (k: string, v: any) => setForm((s) => ({ ...s, [k]: v }));

  const onSave = () => {
    save.mutate({
      id,
      kind: form.kind,
      salutation:
        form.kind === "person" && form.salutation ? form.salutation : null,
      // Privatperson: kein eigenes Namensfeld — der Anzeigename ergibt sich aus Vor- und Nachname.
      name:
        form.kind === "person"
          ? `${form.firstName ?? ""} ${form.lastName ?? ""}`.trim() ||
            form.name?.trim() ||
            c.name
          : form.name?.trim() || c.name,
      firstName: form.firstName || null,
      lastName: form.lastName || null,
      companyName: form.companyName || null,
      customerNumber: form.customerNumber || null,
      vatId: form.vatId || null,
      isBaubetrieb: !!form.isBaubetrieb,
      email: form.email || null,
      phone: form.phone || null,
      website: form.website || null,
      paymentTermsDays:
        form.paymentTermsDays === "" || form.paymentTermsDays == null
          ? null
          : Number(form.paymentTermsDays),
      isActive: !!form.isActive,
      notes: form.notes || null,
    });
    return true;
  };
  guard.setSave(onSave);

  return (
    <div className="flex flex-col h-full" {...guard.rootProps}>
      {guard.dialog}
      <PageHeader
        title={c.name}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/kunden")}>
              <ArrowLeft className="w-4 h-4" /> Kunden
            </Button>
            <Button variant="danger" onClick={() => setArchiveOpen(true)}>
              Archivieren
            </Button>
            <Button variant="primary" onClick={onSave} loading={save.isPending}>
              Speichern
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-8 max-w-4xl">
        <div className="flex items-center gap-2">
          <Badge tone={c.kind === "company" ? "neutral" : "accent"}>
            {c.kind === "company" ? "Firma" : "Privatperson"}
          </Badge>
          {!c.isActive && <Badge tone="warning">inaktiv</Badge>}
        </div>

        {/* Stammdaten */}
        <section>
          <h2 className="text-sm font-semibold text-text-primary mb-3">
            Stammdaten
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Art"
              value={form.kind ?? "company"}
              onChange={(e) => set("kind", e.target.value)}
              options={[
                { value: "company", label: "Firma" },
                { value: "person", label: "Privatperson" },
              ]}
            />
            {form.kind === "person" && (
              <Select
                label="Anrede"
                value={form.salutation ?? ""}
                onChange={(e) => set("salutation", e.target.value)}
                options={[
                  { value: "", label: "–" },
                  ...SALUTATIONS.map((s) => ({
                    value: s,
                    label: SALUTATION_LABELS[s],
                  })),
                ]}
              />
            )}
            {form.kind !== "person" && (
              <Input
                label="Firmenname"
                value={form.name ?? ""}
                onChange={(e) => set("name", e.target.value)}
              />
            )}
            {form.kind === "person" && (
              <>
                <Input
                  label="Vorname"
                  value={form.firstName ?? ""}
                  onChange={(e) => set("firstName", e.target.value)}
                />
                <Input
                  label="Nachname"
                  value={form.lastName ?? ""}
                  onChange={(e) => set("lastName", e.target.value)}
                />
                <Input
                  label="Firmenzugehörigkeit (optional)"
                  value={form.companyName ?? ""}
                  onChange={(e) => set("companyName", e.target.value)}
                />
              </>
            )}
            <Input
              label="Kundennummer"
              value={form.customerNumber ?? ""}
              onChange={(e) => set("customerNumber", e.target.value)}
              hint="Wird bei neuen Kunden automatisch vergeben; hier bei Bedarf überschreibbar."
            />
            <Input
              label="UID-Nummer"
              value={form.vatId ?? ""}
              onChange={(e) => set("vatId", e.target.value)}
            />
            <Input
              label="Zahlungsziel (Tage) — leer = Standard"
              type="number"
              value={form.paymentTermsDays ?? ""}
              onChange={(e) => set("paymentTermsDays", e.target.value)}
            />
            <Input
              label="E-Mail"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => set("email", e.target.value)}
            />
            <Input
              label="Telefon"
              value={form.phone ?? ""}
              onChange={(e) => set("phone", e.target.value)}
            />
            <Input
              label="Website"
              value={form.website ?? ""}
              onChange={(e) => set("website", e.target.value)}
            />
            <div className="flex items-end gap-6 pb-1.5">
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={!!form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                />
                aktiv
              </label>
            </div>
            <div className="col-span-2">
              <Textarea
                label="Notizen"
                value={form.notes ?? ""}
                onChange={(e) => set("notes", e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Adressen */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text-primary">
              Rechnungsadressen
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAddrDialog({ open: true })}
            >
              <Plus className="w-3.5 h-3.5" /> Rechnungsadresse
            </Button>
          </div>
          {!c.addresses.length ? (
            <p className="text-sm text-text-tertiary">
              Keine Rechnungsadresse erfasst.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {c.addresses.map((a) => (
                <div key={a.id} className="card p-3.5 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-text-secondary uppercase tracking-wide flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />
                      {ADDR_KIND_LABEL[a.kind] ?? a.kind}
                      {a.isPrimary && <Badge tone="accent">primär</Badge>}
                    </span>
                    <div className="flex gap-1">
                      {c.kind === "person" && a.street && (
                        <button
                          className="text-text-tertiary hover:text-accent"
                          title="Als Objekt übernehmen"
                          onClick={() =>
                            createProperty.mutate({
                              customerId: id,
                              label: a.label || "Wohnadresse",
                              systemType: "sonstige",
                              addressStreet: a.street,
                              addressZip: a.zip,
                              addressCity: a.city,
                            })
                          }
                        >
                          <Home className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        className="text-text-tertiary hover:text-text-primary"
                        onClick={() => setAddrDialog({ open: true, edit: a })}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        className="text-text-tertiary hover:text-danger"
                        onClick={() =>
                          removeAddress.mutate({ id: a.id, customerId: id })
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {a.label && (
                    <p className="text-text-primary font-medium">{a.label}</p>
                  )}
                  <p className="text-text-secondary">
                    {a.addition && (
                      <>
                        {a.addition}
                        <br />
                      </>
                    )}
                    {a.street}
                    <br />
                    {a.zip} {a.city}{" "}
                    {a.countryCode !== "AT" ? `(${a.countryCode})` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Ansprechpartner */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text-primary">
              Ansprechpartner
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setContactDialog({ open: true })}
            >
              <Plus className="w-3.5 h-3.5" /> Person
            </Button>
          </div>
          {!c.contacts.length ? (
            <p className="text-sm text-text-tertiary">
              Keine Ansprechpartner erfasst.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {c.contacts.map((p) => (
                <div key={p.id} className="card p-3.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-text-primary font-medium">
                      {p.name}{" "}
                      {p.isPrimary && <Badge tone="accent">primär</Badge>}
                    </span>
                    <div className="flex gap-1">
                      <button
                        className="text-text-tertiary hover:text-text-primary"
                        onClick={() =>
                          setContactDialog({ open: true, edit: p })
                        }
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        className="text-text-tertiary hover:text-danger"
                        onClick={() =>
                          removeContact.mutate({ id: p.id, customerId: id })
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {p.role && (
                    <p className="text-text-tertiary text-xs">{p.role}</p>
                  )}
                  <div className="mt-1 space-y-0.5 text-text-secondary">
                    {p.email && (
                      <p className="flex items-center gap-1.5">
                        <Mail className="w-3 h-3" />
                        {p.email}
                      </p>
                    )}
                    {p.phone && (
                      <p className="flex items-center gap-1.5">
                        <Phone className="w-3 h-3" />
                        {p.phone}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Objekte / Anlagen */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text-primary">
              Objekte (Durchführungsorte)
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPropDialog({ open: true })}
            >
              <Plus className="w-3.5 h-3.5" /> Objekt
            </Button>
          </div>
          {!properties.data?.length ? (
            <p className="text-sm text-text-tertiary">
              Keine Objekte erfasst.
              {c.kind === "company" && (
                <span className="block text-warning mt-1">
                  Firmenkunden sollten mindestens ein Objekt hinterlegt haben.
                </span>
              )}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {properties.data.map((p) => (
                <div key={p.id} className="card p-3.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-text-primary font-medium">
                      {p.label}
                    </span>
                    <div className="flex gap-1">
                      <button
                        className="text-text-tertiary hover:text-text-primary"
                        onClick={() =>
                          setPropDialog({ open: true, propertyId: p.id })
                        }
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        className="text-text-tertiary hover:text-danger"
                        onClick={() => removeProperty.mutate({ id: p.id })}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {p.systemType !== "sonstige" && (
                    <p className="text-text-tertiary text-xs">
                      Anlage:{" "}
                      {PROPERTY_SYSTEM_TYPE_LABELS[
                        p.systemType as keyof typeof PROPERTY_SYSTEM_TYPE_LABELS
                      ] ?? p.systemType}
                    </p>
                  )}
                  {(p.addressStreet || p.addressCity) && (
                    <p className="text-text-secondary mt-1">
                      {p.addressStreet}
                      {p.addressStreet && (p.addressZip || p.addressCity)
                        ? ", "
                        : ""}
                      {p.addressZip} {p.addressCity}
                    </p>
                  )}
                  {p._count.contracts > 0 && (
                    <Badge tone="accent">
                      {p._count.contracts} Vertrag/Verträge
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Aufträge */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-text-primary">
              Aufträge
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOrderDialog({ open: true })}
            >
              <Plus className="w-3.5 h-3.5" /> Auftrag
            </Button>
          </div>
          {!orders.data?.length ? (
            <p className="text-sm text-text-tertiary">
              Keine Aufträge erfasst.
            </p>
          ) : (
            <div className="space-y-1.5">
              {orders.data.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => openOrder(o.id)}
                  className="w-full flex items-center justify-between card p-2.5 text-sm hover:bg-bg-hover text-left"
                >
                  <span className="min-w-0 flex items-center gap-1.5">
                    <Wrench className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0" />
                    <span className="text-text-primary truncate">
                      {o.number ?? "ohne Nr."} · {o.title}
                    </span>
                    {o.property && (
                      <span className="text-2xs text-text-tertiary flex-shrink-0">
                        ({o.property.label})
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    {o.scheduledDate && (
                      <span className="text-2xs text-text-tertiary">
                        {new Date(o.scheduledDate).toLocaleDateString("de-AT")}
                      </span>
                    )}
                    <Badge tone={ORDER_STATUS_TONE[o.status] ?? "neutral"}>
                      {ORDER_STATUS_LABELS[
                        o.status as keyof typeof ORDER_STATUS_LABELS
                      ] ?? o.status}
                    </Badge>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Angebote & Rechnungen */}
        <section>
          <h2 className="text-sm font-semibold text-text-primary mb-3">
            Angebote &amp; Rechnungen
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-2">
                Angebote
              </p>
              {!quotes.data?.length ? (
                <p className="text-sm text-text-tertiary">Keine Angebote.</p>
              ) : (
                <div className="space-y-1.5">
                  {quotes.data.map((q) => (
                    <Link
                      key={q.id}
                      to={`/angebote/${q.id}`}
                      className="flex items-center justify-between card p-2.5 text-sm hover:bg-bg-hover"
                    >
                      <span className="min-w-0">
                        <span className="block text-text-primary truncate">
                          {q.number ?? "Entwurf"} · {q.title}
                        </span>
                        <span className="block text-2xs text-text-tertiary">
                          {new Date(q.issueDate).toLocaleDateString("de-AT")} ·{" "}
                          {QUOTE_STATUS_LABEL[q.status] ?? q.status}
                        </span>
                      </span>
                      <span className="text-money text-text-secondary flex-shrink-0 ml-3">
                        {formatMoney(q.grossCents)}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-2">
                Rechnungen
              </p>
              {!invoices.data?.length ? (
                <p className="text-sm text-text-tertiary">Keine Rechnungen.</p>
              ) : (
                <div className="space-y-1.5">
                  {invoices.data.map((i) => (
                    <Link
                      key={i.id}
                      to={`/rechnungen/${i.id}`}
                      className="flex items-center justify-between card p-2.5 text-sm hover:bg-bg-hover"
                    >
                      <span className="min-w-0">
                        <span className="block text-text-primary truncate">
                          {i.number ?? "Entwurf"} · {i.title}
                        </span>
                        <span className="block text-2xs text-text-tertiary">
                          {new Date(i.issueDate).toLocaleDateString("de-AT")} ·{" "}
                          {INVOICE_STATUS_LABEL[i.status] ?? i.status}
                        </span>
                      </span>
                      <span className="text-money text-text-secondary flex-shrink-0 ml-3">
                        {formatMoney(i.grossCents)}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Verlauf */}
        <JournalSection customerId={id} />
      </div>

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="Kunde archivieren?"
        message="Der Kunde wird ausgeblendet, bleibt aber mit allen Daten erhalten (Aufbewahrungspflicht)."
        confirmLabel="Archivieren"
        danger
        onConfirm={() => archive.mutate({ id })}
      />
      <AddressDialog
        customerId={id}
        state={addrDialog}
        onClose={() => setAddrDialog({ open: false })}
      />
      <ContactDialog
        customerId={id}
        state={contactDialog}
        onClose={() => setContactDialog({ open: false })}
      />
      <PropertyDialog
        state={{
          open: propDialog.open,
          customerId: id,
          propertyId: propDialog.propertyId,
        }}
        onClose={() => setPropDialog({ open: false })}
      />
      <OrderDialog
        state={{
          open: orderDialog.open,
          customerId: id,
          orderId: orderDialog.orderId,
        }}
        onClose={closeOrderDialog}
      />
    </div>
  );
}
